import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MessageSquare, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const PROMPTS: Record<string, string> = {
  day_1: "What's the first thing you wanted to do but couldn't figure out?",
  day_3: "What's blocking you from sending your first cycle?",
  day_7_no_template: "What kind of template would help you? Describe it in a sentence or two.",
  day_14_no_cycle: "You've got a template — what's stopping you from sending the cycle?",
  day_30_final: "If Upbrief isn't right for you, what's the reason?",
  reengage_45_idle: "Why did you stop using Upbrief? Anything we could change?",
  general: "What's on your mind? Bugs, ideas, friction — anything helps.",
};

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState<number | null>(null);
  const [nudgeType, setNudgeType] = useState<string>("general");
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();

  // Auto-open from email deep-link: /dashboard?feedback=day_7_no_template
  useEffect(() => {
    const f = params.get("feedback");
    if (f && PROMPTS[f]) {
      setNudgeType(f);
      setOpen(true);
      const next = new URLSearchParams(params);
      next.delete("feedback");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const prompt = PROMPTS[nudgeType] ?? PROMPTS.general;

  const submit = async () => {
    if (message.trim().length < 3) {
      toast.error("Please add a bit more detail");
      return;
    }
    setSubmitting(true);
    const payload = {
      user_id: user?.id ?? null,
      email: user?.email ?? null,
      nudge_type: nudgeType,
      prompt,
      rating,
      message: message.trim().slice(0, 4000),
      page_context: window.location.pathname,
      user_agent: navigator.userAgent.slice(0, 500),
    };
    const { error } = await supabase.from("user_feedback").insert(payload);
    // Fire-and-forget email notification to Devin
    supabase.functions.invoke("notify-feedback", { body: payload }).catch(() => {});
    setSubmitting(false);
    if (error) {
      toast.error("Couldn't send — try again");
      return;
    }
    toast.success("Thanks — Devin reads every one of these");
    setMessage("");
    setRating(null);
    setNudgeType("general");
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-40 shadow-md bg-card hover:bg-accent"
      >
        <MessageSquare className="w-4 h-4 mr-2" />
        Feedback
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Quick feedback</DialogTitle>
            <DialogDescription>{prompt}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="fb-message">Your message</Label>
              <Textarea
                id="fb-message"
                rows={5}
                placeholder="A sentence or two is plenty…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={4000}
              />
            </div>

            <div className="space-y-2">
              <Label>How are we doing? (optional)</Label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(rating === n ? null : n)}
                    className={`w-9 h-9 rounded-md border text-sm font-medium transition-colors ${
                      rating === n
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background hover:bg-accent border-border"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send feedback"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}